import React, { Activity } from "react";
import cx from "classnames";
import { AnimatePresence } from "motion/react";
import { useIsLoggedIn } from "@/features/auth/hooks/useIsLoggedIn";
import { useShellStore } from "@/features/shell/stores/shellStore";
import { usePendingBetsCount } from "hooks/firestore/betting/useEntries";
import {
  Tab,
  TabContent,
  Tabs,
  TabsList,
} from "@/features/shell/components/shared/Tabs/Tabs";
import { ScrollArea } from "@picklebet/ui";
import { QuickBalance } from "@/features/wallet/components/QuickBalance";
import PendingBets from "@/features/betslip/components/PendingBets";
import SettledBets from "@/features/betslip/components/SettledBets";
import { useBetslipStore } from "@/features/betslip/stores/betslipStore";
import { useAutoPruneByPermissions } from "@/features/betslip/hooks/useAutoPruneByPermissions";
import { useSyncSession } from "@/features/betslip/hooks/useSyncSession";
import { useBetslipTab } from "@/features/betslip/hooks/useBetslipTab";
import { OutcomeSubscriptions } from "@/features/betslip/components/OutcomeSubscriptions";
import { SlipBody } from "@/features/betslip/components/SlipBody";
import { PinnedAlerts } from "@/features/betslip/components/PinnedAlerts";
import { SlipFooter } from "@/features/betslip/components/SlipFooter";
import { EntriesFooter } from "@/features/betslip/components/EntriesFooter";
import { DepositModal } from "@/features/wallet/components/deposit/components/deposit-modal/DepositModal";
import { BETSLIP_EXCLUDED_DEPOSIT_METHODS } from "@/features/wallet/components/deposit/constants";
import { useIsQuickDepositAvailable } from "@/features/wallet/hooks/useIsQuickDepositAvailable";
import { useCashAvailable } from "hooks/firestore/user/useAccountBalances";
import { QuickPreferences } from "@/features/betslip/components/overlays/QuickPreferences";
import { Duplicate } from "@/features/betslip/components/overlays/Duplicate";
import { Intercepted } from "@/features/betslip/components/overlays/Intercepted";
import { Delayed } from "@/features/betslip/components/overlays/Delayed";
import { useDisplayAtMost } from "@/hooks/useDisplay";
import AvailableBalancePillNotice from "@/features/wallet/components/AvailableBalancePillNotice";

import { ChevronDownIcon as ChevronDown } from "@picklebet/ui";

import * as styles from "@/features/betslip/components/Betslip.module.scss";

type BetslipProps = {
  isVisible?: boolean;
};

export const Betslip = ({ isVisible = true }: BetslipProps) => {
  useAutoPruneByPermissions();
  useSyncSession();

  const isLoggedIn = useIsLoggedIn();
  const closeDock = useShellStore((s) => s.closeDock);
  const isTabletOrMobile = useDisplayAtMost("tablet");
  const pendingBetsCount = usePendingBetsCount("BET");

  const [tab, setTab] = useBetslipTab();

  const isQuickDepositAvailable = useIsQuickDepositAvailable();

  const selectionsCount = useBetslipStore((s) => s.selectionsCount);
  const overlayType = useBetslipStore((s) => s.overlay.type);
  const setOverlay = useBetslipStore((s) => s.setOverlay);

  // Bonus-Cash-funded stakes draw from the promo balance, so only the
  // cash-funded portion counts against the account balance.
  const totalStakeWithoutPromoCash = useBetslipStore(
    (s) => s.totalStakeWithoutPromoCash,
  );
  const { available: availableBalance } = useCashAvailable();
  const requiredDepositAmount = Math.max(
    0,
    totalStakeWithoutPromoCash - availableBalance,
  );

  const formatBadge = (count: number) => (count < 10 ? count.toString() : "9+");

  return (
    <>
      {/* Outside the Activity boundary: hiding tears down effects, and odds have
          to keep syncing while the slip is closed or a reopened slip could place
          a bet at a stale price. */}
      <OutcomeSubscriptions />

      <Activity mode={isVisible ? "visible" : "hidden"}>
        <div data-testid="betslip" className={styles.container}>
          <Tabs className={styles.tabs} value={tab} onValueChange={setTab}>
            <TabsList
              containerClassName={styles.tabListContainer}
              className={styles.tabList}
              widget={
                <>
                  <div className={styles.close} onClick={closeDock}>
                    <ChevronDown />
                  </div>
                  {isLoggedIn ? (
                    <>
                      <QuickBalance
                        className={styles.quickBalance}
                        showAvatar={false}
                      />
                      {isTabletOrMobile && (
                        <AvailableBalancePillNotice
                          offsetTopAvailable="-3rem"
                          offsetTopPromo="-0.835rem"
                        />
                      )}
                    </>
                  ) : null}
                </>
              }
            >
              <Tab badge={formatBadge(selectionsCount)} value="active">
                Betslip
              </Tab>
              {isLoggedIn && (
                <>
                  <Tab value="pending" badge={formatBadge(pendingBetsCount)}>
                    Pending
                  </Tab>
                  <Tab value="settled">Settled</Tab>
                </>
              )}
            </TabsList>
            {/* Alerts must sit above scroll area */}
            {tab === "active" && <PinnedAlerts />}
            <ScrollArea className={styles.scrollArea} orientation="vertical">
              <div
                className={cx(styles.content, {
                  [styles.entriesTab]: tab !== "active",
                })}
              >
                {tab === "active" && (
                  <TabContent forceMount className={styles.tab} value="active">
                    <SlipBody />
                  </TabContent>
                )}

                {tab === "pending" && (
                  <TabContent className={styles.tab} value="pending">
                    <PendingBets />
                  </TabContent>
                )}

                {tab === "settled" && (
                  <TabContent className={styles.tab} value="settled">
                    <SettledBets />
                  </TabContent>
                )}
              </div>
            </ScrollArea>
          </Tabs>

          {tab === "active" && <SlipFooter />}
          {tab !== "active" && <EntriesFooter tab={tab} />}

          <QuickPreferences />

          <AnimatePresence>
            {overlayType === "duplicate" && <Duplicate />}
          </AnimatePresence>
          {/* One presence for both held states — a delayed bet can turn into a
              trader review, and separate blocks would overlap the two slide-outs
              mid-transition. */}
          <AnimatePresence>
            {overlayType === "intercepted" && <Intercepted key="intercepted" />}
            {overlayType === "delayed" && <Delayed key="delayed" />}
          </AnimatePresence>

          <DepositModal
            isOpen={overlayType === "deposit"}
            onOpenChange={(open) => {
              if (!open) setOverlay({ type: "none" });
            }}
            initialAmountCents={requiredDepositAmount}
            excludedMethods={
              isQuickDepositAvailable
                ? BETSLIP_EXCLUDED_DEPOSIT_METHODS
                : undefined
            }
            warnBetslipLoss
            closeOnSuccess
          />
        </div>
      </Activity>
    </>
  );
};
